import { useState } from 'react';
import { FeatherIcon } from './FeatherIcon';
import { FanslyLogo, normalizePlatform, type Platform } from './PlatformLogo';

export interface AccountAvatarProps {
  avatarUrl: string | null | undefined;
  size: number;
  className?: string;
  /**
   * Account platform (ADR 0019). When 'fansly', a small Fansly flag is overlaid
   * on the avatar's corner. null/absent/'onlyfans' render no badge (OnlyFans is
   * the default platform, so it needs no marker).
   */
  platform?: Platform;
  /** Which corner the Fansly flag sits in. Defaults to bottom-right. */
  flagCorner?: 'bottom-right' | 'top-right';
}

const FLAG_CORNER_CLASS: Record<NonNullable<AccountAvatarProps['flagCorner']>, string> = {
  'bottom-right': '-bottom-0.5 -right-0.5',
  'top-right': '-top-0.5 -right-0.5',
};

export function AccountAvatar({
  avatarUrl,
  size,
  className = '',
  platform,
  flagCorner = 'bottom-right',
}: AccountAvatarProps) {
  const [imageError, setImageError] = useState(false);
  const showImage = Boolean(avatarUrl?.trim()) && !imageError;
  const iconSize = Math.round(size * 0.55);
  const showFanslyFlag = normalizePlatform(platform) === 'fansly';
  // Badge scales with the avatar but stays legible on tiny (20px) avatars.
  const badgeSize = Math.max(11, Math.round(size * 0.42));

  const containerStyle = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
  };

  return (
    <div className={`relative shrink-0 ${className}`} style={containerStyle}>
      <div
        className="w-full h-full overflow-hidden rounded-full bg-[var(--color-bg-hover)] flex items-center justify-center"
      >
        {showImage ? (
          <img
            src={avatarUrl!}
            alt=""
            width={size}
            height={size}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <FeatherIcon name="user" size={iconSize} className="opacity-70" />
        )}
      </div>
      {showFanslyFlag && (
        <FanslyLogo
          size={badgeSize}
          title="Fansly"
          className={`absolute ${FLAG_CORNER_CLASS[flagCorner]} rounded-[3px] ring-2 ring-[var(--color-bg-secondary)]`}
        />
      )}
    </div>
  );
}
